/**
 * AgentTaskViewer - Live feed of tool calls and task status
 * Polls GET /api/agent/tasks/:taskId?include=logs and displays progress
 */

import React, { useEffect, useState, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { AgentToolCallsBar, buildToolPairsFromLogs } from "./AgentToolCallsBar";
import "./AgentTaskViewer.css";

const getApiBase = () => {
    if (process.env.NODE_ENV === "production") {
        return process.env.REACT_APP_BACKEND_URL || "";
    }
    return process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
};

export interface AgentTaskViewerProps {
    taskId: string;
    onComplete?: () => void;
    darkMode?: boolean;
}

interface LogEntry {
    type: string;
    tool_name?: string;
    tool_input?: Record<string, unknown>;
    tool_output?: string;
    content?: string;
    step_number?: number;
    created_at?: string;
}

interface TaskData {
    id: string;
    title: string;
    status: string;
    result_summary?: string;
    logs?: LogEntry[];
}

function formatLogLine(log: LogEntry): { icon: string; text: string } | null {
    const type = log.type || "unknown";
    if (type === "tool_call") {
        const name = log.tool_name || "?";
        const input = log.tool_input;
        const argStr = input?.query ? `"${String(input.query).slice(0, 40)}..."` : input ? JSON.stringify(input).slice(0, 40) : "";
        return { icon: "🔧", text: `${name}(${argStr})` };
    }
    if (type === "tool_result") {
        const name = log.tool_name || "?";
        const ok = log.tool_output && String(log.tool_output).includes('"success":true');
        return { icon: ok ? "✓" : "→", text: `${name} result` };
    }
    if (type === "thinking") return null;
    if (type === "error") return { icon: "✗", text: (log.content || "Error").slice(0, 100) };
    if (type === "summary") return { icon: "📋", text: "Context compressed" };
    return null;
}

export const AgentTaskViewer: React.FC<AgentTaskViewerProps> = ({
    taskId,
    onComplete,
    darkMode = false,
}) => {
    const [task, setTask] = useState<TaskData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const logsEndRef = useRef<HTMLDivElement>(null);
    const prevStatusRef = useRef<string | null>(null);

    useEffect(() => {
        if (!taskId) return;

        const poll = async () => {
            try {
                const res = await fetch(
                    `${getApiBase()}/api/agent/tasks/${encodeURIComponent(taskId)}?include=logs`
                );
                if (!res.ok) throw new Error("Failed to fetch task");
                const data = await res.json();
                setTask(data);
                setError(null);

                if (data.status === "completed" || data.status === "failed") {
                    onComplete?.();
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to load");
            }
        };

        poll();
        const interval = setInterval(poll, 2500);
        return () => clearInterval(interval);
    }, [taskId, onComplete]);

    useEffect(() => {
        if (task?.logs?.length) {
            logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [task?.logs?.length]);

    useEffect(() => {
        if (task?.status && (task.status === "completed" || task.status === "failed")) {
            prevStatusRef.current = task.status;
        }
    }, [task?.status]);

    if (error) {
        return (
            <div className={`agent-task-viewer ${darkMode ? "dark" : ""}`}>
                <div className="agent-task-error">Error: {error}</div>
            </div>
        );
    }

    if (!task) {
        return (
            <div className={`agent-task-viewer ${darkMode ? "dark" : ""}`}>
                <div className="agent-task-loading">Loading...</div>
            </div>
        );
    }

    const lines = (task.logs || [])
        .map(formatLogLine)
        .filter((x): x is { icon: string; text: string } => x !== null);

    const isDone = task.status === "completed" || task.status === "failed";
    const toolPairs = buildToolPairsFromLogs(task.logs || []);

    return (
        <div className={`agent-task-viewer ${darkMode ? "dark" : ""}`}>
            <div className="agent-task-header">
                <span className="agent-task-title">{task.title}</span>
                <span className={`agent-task-status status-${task.status}`}>{task.status}</span>
            </div>
            <div className="agent-task-log-feed">
                {lines.length === 0 && !isDone && (
                    <div className="agent-task-waiting">Waiting for worker to pick up task...</div>
                )}
                {lines.map((line, i) => (
                    <div key={i} className="agent-task-log-line">
                        <span className="agent-task-log-icon">{line.icon}</span>
                        <span className="agent-task-log-text">{line.text}</span>
                    </div>
                ))}
                <div ref={logsEndRef} />
            </div>
            {isDone && task.result_summary && (
                <div className="agent-task-result">
                    <h4>Result</h4>
                    <div className="agent-task-result-content agent-task-result-scroll">
                        <div className="agent-task-result-markdown">
                            <ReactMarkdown>
                                {task.result_summary.replace(/^TASK_COMPLETE\s*/i, "").trimStart()}
                            </ReactMarkdown>
                        </div>
                    </div>
                </div>
            )}
            <AgentToolCallsBar toolPairs={toolPairs} darkMode={darkMode} />
        </div>
    );
};
